// Formulário de criar/editar lead CRM (Story 11.2).
// - RHF + zodResolver (createCrmLeadSchema do @control-center/shared — fonte única)
// - Máscara inline no telefone (reuso de masks)
// - Validação onBlur (erros) + onChange (limpa) — padrão ClientForm
// - Submit desabilitado enquanto pending (AC10)
// - Avisos de dedup exibidos acima do form quando presentes (FR-50.9 / AC2)

import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle } from 'lucide-react';
import type { z } from 'zod';

import { createCrmLeadSchema, type CrmLeadDuplicateWarning } from '@control-center/shared';

import { maskPhone, unmaskDigits } from '../../lib/masks';
import { toast } from 'sonner';

import { firstErrorMessage } from '../../lib/form-errors';
import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

const ORIGIN_OPTIONS: { value: string; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'indicacao', label: 'Indicação' },
  { value: 'trafego_pago', label: 'Tráfego pago' },
  { value: 'site', label: 'Site' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'evento', label: 'Evento' },
  { value: 'prospeccao_ativa', label: 'Prospecção ativa' },
  { value: 'outro', label: 'Outro' },
];

const TEMPERATURE_OPTIONS: { value: string; label: string }[] = [
  { value: 'frio', label: 'Frio' },
  { value: 'morno', label: 'Morno' },
  { value: 'quente', label: 'Quente' },
];

const POTENTIAL_OPTIONS: { value: string; label: string }[] = [
  { value: 'low', label: 'Baixo' },
  { value: 'medium', label: 'Médio' },
  { value: 'high', label: 'Alto' },
];

export interface SaasProductOption {
  id: string;
  name: string;
}

export interface ResponsibleUserOption {
  id: string;
  name: string;
}

export type LeadFormValues = z.input<typeof createCrmLeadSchema>;
export type LeadFormSubmit = z.output<typeof createCrmLeadSchema>;

interface LeadFormProps {
  initialValues?: Partial<LeadFormValues>;
  isPending?: boolean;
  submitLabel?: string;
  saasProducts?: SaasProductOption[];
  /** Usuários ativos pro picker de responsável (Story 11.4). */
  responsibleUsers?: ResponsibleUserOption[];
  /** Avisos de dedup do último POST (FR-50.9). Exibidos no topo. */
  warnings?: CrmLeadDuplicateWarning[];
  onSubmit: (values: LeadFormSubmit) => void | Promise<void>;
  onCancel?: () => void;
}

const DEFAULT_VALUES: LeadFormValues = {
  name: '',
  company: null,
  segment: null,
  phone: null,
  email: null,
  origin: 'outro',
  saas_product_id: null,
  responsible_user_id: null,
  pain_point: null,
  potential: 'medium',
  temperature: 'frio',
  next_action: null,
  next_contact_date: null,
  observations: null,
};

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-3 text-[13px] font-semibold tracking-[-0.005em] text-ink">{children}</h2>
  );
}

function Card({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">{children}</section>
  );
}

function emptyToNull(v: string): string | null {
  return v === '' ? null : v;
}

const WARNING_FIELD_LABEL: Record<CrmLeadDuplicateWarning['match_field'], string> = {
  phone: 'telefone',
  email: 'e-mail',
  document: 'documento',
};

export function LeadForm({
  initialValues,
  isPending,
  submitLabel = 'Salvar',
  saasProducts = [],
  responsibleUsers = [],
  warnings = [],
  onSubmit,
  onCancel,
}: LeadFormProps): JSX.Element {
  const merged: LeadFormValues = { ...DEFAULT_VALUES, ...initialValues };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LeadFormValues>({
    resolver: zodResolver(createCrmLeadSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: merged,
  });

  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const { ref: nameRefRHF, ...nameRest } = register('name');

  const handleValid = (values: LeadFormValues): void | Promise<void> => {
    const parsed = createCrmLeadSchema.parse(values);
    return onSubmit(parsed);
  };

  return (
    <form
      onSubmit={handleSubmit(handleValid, (errs) =>
        toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
      )}
      className="flex flex-col gap-4"
      noValidate
    >
      {warnings.length > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-[14px] border border-warn/40 bg-warn/5 p-4 text-[12px] text-ink-2"
        >
          <AlertTriangle
            size={15}
            strokeWidth={2}
            className="mt-[1px] shrink-0 text-warn"
            aria-hidden
          />
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-ink">Possível duplicata</span>
            <ul className="flex flex-col gap-[2px]">
              {warnings.map((w) => (
                <li key={`${w.type}-${w.matched_id}`}>
                  {w.type === 'duplicate_client' ? 'Cliente' : 'Lead'}{' '}
                  <strong>{w.matched_name}</strong> já usa esse {WARNING_FIELD_LABEL[w.match_field]}
                  . O cadastro foi salvo mesmo assim.
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Identificação */}
      <Card>
        <SectionTitle>Identificação</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Nome" required error={errors.name?.message} className="md:col-span-2">
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Nome do contato ou empresa"
                hasError={!!errors.name}
                ref={(el) => {
                  nameRefRHF(el);
                  firstFieldRef.current = el;
                }}
                {...nameRest}
              />
            )}
          </Field>

          <Field label="Empresa" error={errors.company?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Opcional"
                hasError={!!errors.company}
                {...register('company', { setValueAs: emptyToNull })}
              />
            )}
          </Field>

          <Field label="Segmento" error={errors.segment?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Ex: estética, advocacia"
                hasError={!!errors.segment}
                {...register('segment', { setValueAs: emptyToNull })}
              />
            )}
          </Field>

          <Field label="Produto SaaS de interesse" error={errors.saas_product_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.saas_product_id}
                defaultValue=""
                {...register('saas_product_id', { setValueAs: emptyToNull })}
              >
                <option value="">—</option>
                {saasProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Responsável" error={errors.responsible_user_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.responsible_user_id}
                defaultValue=""
                {...register('responsible_user_id', { setValueAs: emptyToNull })}
              >
                <option value="">Sem responsável</option>
                {responsibleUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {/* Contato */}
      <Card>
        <SectionTitle>Contato</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Telefone" error={errors.phone?.message}>
            {({ inputId, describedBy }) => (
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    tabular
                    inputMode="tel"
                    placeholder="(00) 00000-0000"
                    hasError={!!errors.phone}
                    value={maskPhone(field.value ?? '')}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(unmaskDigits(e.target.value) || null)}
                  />
                )}
              />
            )}
          </Field>

          <Field label="E-mail" error={errors.email?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="email"
                inputMode="email"
                placeholder="contato@empresa.com"
                hasError={!!errors.email}
                {...register('email', { setValueAs: emptyToNull })}
              />
            )}
          </Field>
        </div>
      </Card>

      {/* Qualificação */}
      <Card>
        <SectionTitle>Qualificação</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Origem" error={errors.origin?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.origin}
                {...register('origin')}
              >
                {ORIGIN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Temperatura" error={errors.temperature?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.temperature}
                {...register('temperature')}
              >
                {TEMPERATURE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Potencial" error={errors.potential?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.potential}
                {...register('potential')}
              >
                {POTENTIAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Dor / necessidade"
            error={errors.pain_point?.message}
            className="md:col-span-3"
          >
            {({ inputId, describedBy }) => (
              <Textarea
                id={inputId}
                aria-describedby={describedBy}
                rows={2}
                placeholder="O que o lead precisa resolver"
                hasError={!!errors.pain_point}
                {...register('pain_point', { setValueAs: emptyToNull })}
              />
            )}
          </Field>
        </div>
      </Card>

      {/* Próximo passo */}
      <Card>
        <SectionTitle>Próximo passo</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Próxima ação" error={errors.next_action?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Ex: ligar, enviar proposta"
                hasError={!!errors.next_action}
                {...register('next_action', { setValueAs: emptyToNull })}
              />
            )}
          </Field>

          <Field
            label="Data do próximo contato"
            error={errors.next_contact_date?.message}
            hint="Use a agenda — datas passadas são permitidas"
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="date"
                hasError={!!errors.next_contact_date}
                {...register('next_contact_date', { setValueAs: emptyToNull })}
              />
            )}
          </Field>

          <Field label="Observações" error={errors.observations?.message} className="md:col-span-2">
            {({ inputId, describedBy }) => (
              <Textarea
                id={inputId}
                aria-describedby={describedBy}
                rows={3}
                placeholder="Notas internas"
                hasError={!!errors.observations}
                {...register('observations', { setValueAs: emptyToNull })}
              />
            )}
          </Field>
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-[22px] flex items-center justify-end gap-2 border-t border-line bg-page/90 px-[22px] py-3 backdrop-blur">
        {onCancel && (
          <PillButton onClick={onCancel} disabled={isPending || isSubmitting}>
            Cancelar
          </PillButton>
        )}
        <PillButton solid type="submit" disabled={isPending || isSubmitting || !isValid}>
          {isPending || isSubmitting ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}
